import { useState, useEffect } from "react";
import type { DiagnosticUploadInfo, DiagnosticFile } from "@/services/SupportService";
import { SupportService } from "@/services/SupportService";
import { FilePreviewModal } from "./FilePreviewModal";

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

const formatFileDate = (timestamp: number): string => {
  const date = new Date(timestamp);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

interface SupportUploadCardProps {
  upload: DiagnosticUploadInfo;
  onUpdate: () => void;
  isExpanded?: boolean;
  onExpandChange?: (isExpanded: boolean) => void;
}

const formatDate = (dateString?: string): string => {
  if (!dateString) return "-";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getPlatformIcon = (platform?: string): JSX.Element => {
  const platformLower = platform?.toLowerCase() || "";

  if (platformLower === "wearos") {
    return (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    );
  }

  if (platformLower === "ios_watch" || platformLower === "ios") {
    return (
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
        <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
      </svg>
    );
  }

  if (platformLower === "android") {
    return (
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
        <path d="M6 18c0 .55.45 1 1 1h1v3.5c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5V19h2v3.5c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5V19h1c.55 0 1-.45 1-1V8H6v10zM3.5 8C2.67 8 2 8.67 2 9.5v7c0 .83.67 1.5 1.5 1.5S5 17.33 5 16.5v-7C5 8.67 4.33 8 3.5 8zm17 0c-.83 0-1.5.67-1.5 1.5v7c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5v-7c0-.83-.67-1.5-1.5-1.5z" />
      </svg>
    );
  }

  // Default device icon
  return (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
    </svg>
  );
};

const getPlatformLabel = (platform?: string): string => {
  const platformLower = platform?.toLowerCase() || "";
  if (platformLower === "wearos") return "Wear OS";
  if (platformLower === "ios_watch") return "Apple Watch";
  if (platformLower === "ios") return "iOS";
  if (platformLower === "android") return "Android";
  return platform || "Unknown";
};

const getPlatformColor = (platform?: string): string => {
  const platformLower = platform?.toLowerCase() || "";
  if (platformLower === "wearos") return "bg-green-100 text-green-700";
  if (platformLower === "ios_watch" || platformLower === "ios") return "bg-gray-100 text-gray-700";
  if (platformLower === "android") return "bg-green-100 text-green-700";
  return "bg-gray-100 text-gray-600";
};

export const SupportUploadCard: React.FC<SupportUploadCardProps> = ({
  upload,
  onUpdate,
  isExpanded: controlledExpanded,
  onExpandChange,
}) => {
  // Support both controlled and uncontrolled expansion
  const [internalExpanded, setInternalExpanded] = useState(false);
  const isExpanded = controlledExpanded !== undefined ? controlledExpanded : internalExpanded;
  const [files, setFiles] = useState<DiagnosticFile[]>([]);
  const [isLoadingFiles, setIsLoadingFiles] = useState(false);
  const [notes, setNotes] = useState(upload.notes || "");
  const [analyzed, setAnalyzed] = useState(upload.analyzed);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [previewFile, setPreviewFile] = useState<string | null>(null);

  // Load files when expanded via controlled prop (deep link)
  useEffect(() => {
    const loadFiles = async () => {
      if (isExpanded && files.length === 0 && !isLoadingFiles) {
        setIsLoadingFiles(true);
        try {
          const fileList = await SupportService.listFiles(upload.uploadId);
          const normalizedFiles = fileList.map((item: any) => {
            if (typeof item === "string") {
              return { name: item, size: 0, lastModified: 0 };
            }
            return item;
          });
          setFiles(normalizedFiles);
        } catch (error) {
          console.error("Error loading files:", error);
        } finally {
          setIsLoadingFiles(false);
        }
      }
    };
    loadFiles();
  }, [isExpanded, files.length, isLoadingFiles, upload.uploadId]);

  const handleExpand = () => {
    const newExpanded = !isExpanded;
    if (onExpandChange) {
      onExpandChange(newExpanded);
    } else {
      setInternalExpanded(newExpanded);
    }
    // File loading is handled by useEffect
  };

  const handleDownload = (filename: string) => {
    const url = SupportService.getDownloadUrl(upload.uploadId, filename);
    window.open(url, "_blank");
  };

  const handleDownloadAll = () => {
    const url = SupportService.getDownloadAllUrl(upload.uploadId);
    window.open(url, "_blank");
  };

  const handlePreview = (filename: string) => {
    setPreviewFile(filename);
  };

  const canPreview = (filename: string): boolean => {
    const lowerName = filename.toLowerCase();
    // Check extension
    if (lowerName.endsWith(".json") || lowerName.endsWith(".txt") || lowerName.endsWith(".log")) {
      return true;
    }
    // Also allow files that contain "log" in the name (like error_log)
    if (lowerName.includes("log") || lowerName.includes("error")) {
      return true;
    }
    return false;
  };

  const handleNotesChange = (value: string) => {
    setNotes(value);
    setHasChanges(true);
  };

  const handleAnalyzedChange = (value: boolean) => {
    setAnalyzed(value);
    setHasChanges(true);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await SupportService.updateUpload(upload.uploadId, {
        analyzed,
        notes: notes || null,
      });
      setHasChanges(false);
      onUpdate();
    } catch (error) {
      console.error("Error saving upload:", error);
      alert("Failed to save changes. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const getFileIcon = (filename: string): JSX.Element => {
    if (!filename) {
      return (
        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
      );
    }
    const ext = filename.split(".").pop()?.toLowerCase();

    if (ext === "json") {
      return (
        <svg className="w-4 h-4 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
      );
    }

    if (ext === "log" || ext === "txt") {
      return (
        <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      );
    }

    return (
      <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
      </svg>
    );
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200">
      {/* Header - Always visible */}
      <button
        onClick={handleExpand}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          {/* Folder Icon */}
          <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
            <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
            </svg>
          </div>

          {/* Upload Info */}
          <div className="text-left">
            <div className="flex items-center gap-2">
              <p className="text-sm font-medium text-gray-900 font-mono">
                {upload.uploadId}
              </p>
              {/* Platform Badge */}
              <span className={`px-2 py-0.5 text-xs font-medium rounded-full flex items-center gap-1 ${getPlatformColor(upload.platform)}`}>
                {getPlatformIcon(upload.platform)}
                {getPlatformLabel(upload.platform)}
              </span>
              {/* Analyzed Badge */}
              <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                upload.analyzed
                  ? "bg-green-100 text-green-700"
                  : "bg-orange-100 text-orange-700"
              }`}>
                {upload.analyzed ? "Analyzed" : "Not Analyzed"}
              </span>
            </div>
            <p className="text-xs text-gray-500">
              {upload.deviceModel || "Unknown device"}
              {upload.appVersion && ` • v${upload.appVersion}`}
              {upload.sessionId && ` • Session #${upload.sessionId}`}
              {" • "}
              {formatDate(upload.createdAt)}
            </p>
          </div>
        </div>

        {/* Expand/Collapse Icon */}
        <svg
          className={`w-5 h-5 text-gray-400 transition-transform ${isExpanded ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="px-4 pb-4 border-t border-gray-100">
          {/* Files Section */}
          <div className="mt-4">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-xs font-medium text-gray-700">Files</h4>
              {files.length > 0 && (
                <button
                  onClick={handleDownloadAll}
                  className="text-xs text-cyan-600 hover:text-cyan-700 font-medium flex items-center gap-1"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Download All (ZIP)
                </button>
              )}
            </div>
            {isLoadingFiles ? (
              <div className="flex items-center justify-center py-4">
                <svg className="animate-spin h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
              </div>
            ) : files.length === 0 ? (
              <p className="text-xs text-gray-500 italic py-2">No files found</p>
            ) : (
              <div className="space-y-1">
                {files.map((file) => (
                  <div
                    key={file.name}
                    className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      {getFileIcon(file.name)}
                      <span className="text-sm text-gray-700 truncate">{file.name}</span>
                      {file.size > 0 && (
                        <span className="text-xs text-gray-400 flex-shrink-0">
                          {formatFileSize(file.size)}
                        </span>
                      )}
                      {file.lastModified > 0 && (
                        <span className="text-xs text-gray-400 flex-shrink-0">
                          {formatFileDate(file.lastModified)}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                      {canPreview(file.name) && (
                        <button
                          onClick={() => handlePreview(file.name)}
                          className="text-xs text-purple-600 hover:text-purple-700 font-medium flex items-center gap-1"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                          Preview
                        </button>
                      )}
                      <button
                        onClick={() => handleDownload(file.name)}
                        className="text-xs text-cyan-600 hover:text-cyan-700 font-medium flex items-center gap-1"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                        Download
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Notes Section */}
          <div className="mt-4">
            <h4 className="text-xs font-medium text-gray-700 mb-2">Admin Notes</h4>
            <textarea
              value={notes}
              onChange={(e) => handleNotesChange(e.target.value)}
              placeholder="Add notes about this diagnostic upload..."
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent resize-none"
              rows={3}
            />
          </div>

          {/* Actions */}
          <div className="mt-4 flex items-center justify-between">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={analyzed}
                onChange={(e) => handleAnalyzedChange(e.target.checked)}
                className="w-4 h-4 text-cyan-600 border-gray-300 rounded focus:ring-cyan-500"
              />
              <span className="text-sm text-gray-700">Mark as Analyzed</span>
            </label>

            <button
              onClick={handleSave}
              disabled={!hasChanges || isSaving}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                hasChanges
                  ? "bg-cyan-600 text-white hover:bg-cyan-700"
                  : "bg-gray-100 text-gray-400 cursor-not-allowed"
              }`}
            >
              {isSaving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </div>
      )}

      {/* File Preview Modal */}
      {previewFile && (
        <FilePreviewModal
          isOpen={true}
          onClose={() => setPreviewFile(null)}
          filename={previewFile}
          uploadId={upload.uploadId}
        />
      )}
    </div>
  );
};
